import { Asset } from '@fmnts/api';
import { IFieldCondition } from '../api-model/condition';
import {
  IApiFormBaseField,
  IApiFormSelectInputItem,
} from '../api-model/forms.api';
import {
  FormField,
  IValidatorConfig,
  SelectInputField,
  TextboxField,
  WidgetType,
} from './form-field';

export type Question = 'singleChoice' | 'multipleChoice';

export type QuestionSummary = `result_${Question}`;

export type Information = 'note';

export type QuizFieldType = Question | Information | QuestionSummary;

export type QuizSelectField = QuizField<'input', QuizSelect>;
export type QuizSelectResultField = QuizField<'input', QuizSelect>;

export type QuizTextboxField = QuizField<'input', QuizTextbox>;

/**
 * Api Models of QuizField
 */
export interface IQuizField {
  label: string | undefined;
  widget: QuizFieldType;
  value?: (number | string)[];
}

interface IQuizSelectField {
  defaultValue?: (number | string)[];
  items: QuizSelectFieldItem[];
  itemConditions?: Record<string, IFieldCondition> | undefined;
  scope: 'form' | 'session' | string | undefined;
  validators: IValidatorConfig[];
}

interface IQuizTextboxField {
  assets: Asset[];
  text: string;
}

/**
 * Quiz Field Instance class
 */
export class QuizField<
  TWidgetType extends WidgetType = WidgetType,
  T extends QuizSelect | QuizTextbox | undefined = any,
> extends FormField<TWidgetType, T> {
  /**
   * Quizzes are not translated, so only accepted value is `string`
   */
  override label: string | undefined;
  /**
   * Typed values for quiz-widgets
   */
  override widget: QuizFieldType;

  constructor(
    dto: Readonly<IApiFormBaseField> & Readonly<IQuizField>,
    fieldOptions: T,
    fieldType: TWidgetType,
  ) {
    super(dto, fieldOptions, fieldType);
    this.label = dto.label;
    this.widget = dto.widget;
  }
}

export class QuizSelect extends SelectInputField {
  override items: QuizSelectFieldItem[];
  override defaultValue: string | (string | number)[];

  constructor(dto: IQuizSelectField) {
    super(dto);
    this.items = dto.items || [];
    this.defaultValue = dto.defaultValue || [];
  }
}

export class QuizTextbox extends TextboxField {
  override text: string;
  override assets: Asset[];

  constructor(dto: Readonly<IQuizTextboxField>) {
    super(dto);
    this.text = dto.text;
    this.assets = dto.assets;
  }
}

export type QuizSelectFieldItem = IApiFormSelectInputItem;
